import { AutoComplete, Row, Typography } from 'antd';
import config from 'config';
import {
  AnimalSpecies,
  useGetPetBreedsListLazyQuery,
  useGetPetBreedsListQuery,
} from 'gql/types';
import { Button, Spin } from 'modules/common/components';
import { useDebounce } from 'modules/common/hooks';
import { FC, useCallback, useMemo, useRef, useState } from 'react';
import { Control, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import styles from './styles.module.scss';

type Props = {
  controlled: {
    control: Control<any>;
    name: string;
  };
  species?: AnimalSpecies;
  size?: 'large' | 'middle' | 'small';
  placeholder?: string;
  disabled?: boolean;
};

export const BreedSelect: FC<Props> = ({
  controlled,
  species,
  size = 'middle',
  placeholder,
  disabled = false,
}) => {
  const { t } = useTranslation('donor.BreedSelect');

  const [searchValue, setSearchValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [allLoadedBreeds, setAllLoadedBreeds] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [hasMore, setHasMore] = useState(true);
  const [isSearchPending, setIsSearchPending] = useState(false);
  const isLoadingMoreRef = useRef(false);

  const { loading: initialLoading } = useGetPetBreedsListQuery({
    variables: {
      input: {
        take: config.PAGINATION.BREEDS_PAGE_SIZE,
        skip: 0,
        filter: species ? { species } : undefined,
      },
    },
    skip: !species,
    onCompleted: (data) => {
      const breeds = data.getPetBreedsList.data || [];
      setAllLoadedBreeds(breeds);
      setHasMore(breeds.length === config.PAGINATION.BREEDS_PAGE_SIZE);
    },
  });

  const [fetchBreeds, { loading: fetchLoading }] =
    useGetPetBreedsListLazyQuery();

  const isSearching = isSearchPending || fetchLoading;

  const { debounce } = useDebounce(300);

  const debouncedSearch = useMemo(
    () =>
      debounce(async (query: string) => {
        setCurrentPage(1);

        try {
          const { data } = await fetchBreeds({
            variables: {
              input: {
                take: config.PAGINATION.BREEDS_PAGE_SIZE,
                skip: 0,
                filter: {
                  species: species || undefined,
                  searchingQuery: query || undefined,
                },
              },
            },
          });

          const breeds = data?.getPetBreedsList.data || [];
          setAllLoadedBreeds(breeds);
          setHasMore(breeds.length === config.PAGINATION.BREEDS_PAGE_SIZE);
        } finally {
          setIsSearchPending(false);
        }
      }),
    [debounce, fetchBreeds, species]
  );

  const handleSearch = (value: string) => {
    setSearchValue(value);
    setIsSearchPending(true);
    setIsOpen(true);
    debouncedSearch(value);
  };

  const handleLoadMore = async () => {
    isLoadingMoreRef.current = true;

    const nextPage = currentPage + 1;
    const skip = (nextPage - 1) * config.PAGINATION.BREEDS_PAGE_SIZE;

    const { data } = await fetchBreeds({
      variables: {
        input: {
          take: config.PAGINATION.BREEDS_PAGE_SIZE,
          skip,
          filter: {
            species: species || undefined,
            searchingQuery: searchValue || undefined,
          },
        },
      },
    });

    const newBreeds = data?.getPetBreedsList.data || [];

    setAllLoadedBreeds((prev) => {
      const existingIds = new Set(prev.map((b) => b.id));
      const uniqueNew = newBreeds.filter((b) => !existingIds.has(b.id));
      return [...prev, ...uniqueNew];
    });

    setCurrentPage(nextPage);
    setHasMore(newBreeds.length === config.PAGINATION.BREEDS_PAGE_SIZE);
    isLoadingMoreRef.current = false;
  };

  const prevSpeciesRef = useRef(species);
  if (prevSpeciesRef.current !== species) {
    prevSpeciesRef.current = species;
    setSearchValue('');
    setCurrentPage(1);
    setAllLoadedBreeds([]);
  }

  const options = useMemo(() => {
    const breedOptions = allLoadedBreeds.map((breed) => ({
      value: breed.name,
      label: breed.name,
    }));

    const trimmedSearch = searchValue.trim();
    if (
      trimmedSearch &&
      !allLoadedBreeds.some(
        (b) => b.name.toLowerCase() === trimmedSearch.toLowerCase()
      )
    ) {
      breedOptions.push({
        value: trimmedSearch,
        label: `${t('useCustomBreed')}: "${trimmedSearch}"`,
      });
    }

    return breedOptions;
  }, [allLoadedBreeds, searchValue, t]);

  const dropdownRender = useCallback(
    (menu: React.ReactElement) => (
      <div
        onMouseDown={(e) => {
          e.preventDefault();
        }}
      >
        {initialLoading ? (
          <Row justify="center" className={styles.loadingWrapper}>
            <Spin size="small" />
          </Row>
        ) : (
          <>
            {isSearching && (
              <Row justify="center" className={styles.rowWrapper}>
                <Spin size="small" />
              </Row>
            )}
            {menu}
            {hasMore && !isSearching && (
              <Row justify="center" className={styles.rowWrapper}>
                <Button
                  type="link"
                  onClick={handleLoadMore}
                  loading={fetchLoading}
                >
                  {t('loadMore')}
                </Button>
              </Row>
            )}
            {allLoadedBreeds.length === 0 && !isSearching && (
              <Row justify="center" className={styles.rowWrapper}>
                <Typography.Text type="secondary">
                  {t('noBreedsFound')}
                </Typography.Text>
              </Row>
            )}
          </>
        )}
      </div>
    ),
    [initialLoading, isSearching, hasMore, fetchLoading, allLoadedBreeds, t]
  );

  return (
    <Controller
      name={controlled.name}
      control={controlled.control}
      render={({ field }) => (
        <AutoComplete
          {...field}
          className={styles.autocomplete}
          size={size}
          options={options}
          onSearch={handleSearch}
          onSelect={(value) => {
            field.onChange(value);
            setSearchValue('');
            setIsOpen(false);
          }}
          onChange={(value) => {
            field.onChange(value);
          }}
          open={isOpen}
          onFocus={() => setIsOpen(true)}
          onBlur={() => {
            if (!isLoadingMoreRef.current) {
              setIsOpen(false);
            }
          }}
          placeholder={placeholder}
          disabled={!species || disabled}
          dropdownRender={dropdownRender}
          filterOption={false}
          notFoundContent={null}
        />
      )}
    />
  );
};
